import { useState } from "react";
import { Play, Volume2 } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";
import { SHOWREEL } from "@/data/content";

const VideoCard = ({ video, index, onOpen }) => (
  <Reveal delay={0.08 * (index % 3)}>
    <div
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0A0A0A] transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-electric/50"
      data-testid={`showreel-card-${index}`}
    >
      <div className="relative">
        <video
          poster={video.poster}
          preload="none"
          playsInline
          muted
          className="aspect-video w-full object-cover"
          data-testid={`showreel-video-${index}`}
        >
          <source src={video.src} type="video/mp4" />
          <source src={video.src.replace(".mp4", ".webm")} type="video/webm" />
        </video>
        <button
          type="button"
          onClick={() => onOpen(video)}
          aria-label={`Play full-screen: ${video.title}`}
          data-testid={`showreel-play-${index}`}
          className="absolute inset-0 flex cursor-pointer items-center justify-center bg-black/30 transition-[background-color] duration-300 hover:bg-black/10"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full border border-electric/60 bg-black/60 text-electric backdrop-blur-md transition-[transform,box-shadow] duration-300 group-hover:scale-110 group-hover:shadow-[0_0_30px_rgba(0,163,255,0.5)]">
            <Play size={26} className="ml-1" aria-hidden="true" />
          </span>
        </button>
      </div>
      <div className="flex items-start justify-between gap-3 p-5">
        <div>
          <h3 className="font-display text-base font-semibold uppercase tracking-tight text-neutral-100">{video.title}</h3>
          <p className="mt-1 text-xs text-neutral-500">{video.caption}</p>
        </div>
        <span className="mt-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-electric">
          <Volume2 size={13} aria-hidden="true" /> Sound on
        </span>
      </div>
    </div>
  </Reveal>
);

export default function Showreel() {
  const [viewerIndex, setViewerIndex] = useState(null);
  const viewerItems = SHOWREEL.map((v) => ({
    video: v.src,
    poster: v.poster,
    caption: `${v.title} — ${v.caption}`,
  }));
  return (
    <section id="showreel" data-testid="showreel-section" className="border-t border-white/5 bg-[#0A0A0A] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          label="Showreel"
          title="Live from"
          accent="the stage"
          description="Clips from real shows — arena concerts, skyline DJ nights and intimate sessions across Dubai. Tap any clip for full-screen with sound."
          testId="showreel-heading"
        />
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="showreel-grid">
          {SHOWREEL.map((video, i) => (
            <VideoCard
              key={video.src}
              video={video}
              index={i}
              onOpen={() => setViewerIndex(i)}
            />
          ))}
        </div>
      </div>
      <Lightbox items={viewerItems} index={viewerIndex} onClose={() => setViewerIndex(null)} onNavigate={setViewerIndex} />
    </section>
  );
}
